import type React from "react"
import { useState } from "react"
import { Send, Mail, FileText, User, MessageSquare, CheckCircle, AlertCircle, Copy, Download } from "lucide-react"
import { AgentService } from "@/features/agents"

interface PdfResource {
  id: string
  name: string
  description: string
  filename: string
  category: "gaming" | "privacy" | "followup"
}

const AgentShareInstructions: React.FC = () => {
  const [customerEmail, setCustomerEmail] = useState("")
  const [customerName, setCustomerName] = useState("")
  const [emailSubject, setEmailSubject] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [selectedPdfs, setSelectedPdfs] = useState<string[]>([])

  // PDF Resources
  const pdfResources: PdfResource[] = [
    {
      id: "gamer-tag-guide",
      name: "How to Find Your Gamer Tag",
      description: "Step-by-step guide to locate your gamer tag on Xbox, PlayStation, Steam, and other platforms",
      filename: "how-to-find-gamer-tag.pdf",
      category: "gaming"
    },
    {
      id: "privacy-settings-guide",
      name: "How to Change Privacy Settings",
      description: "Complete guide to adjusting privacy settings for your gamer tag and account",
      filename: "privacy-settings-guide.pdf",
      category: "privacy"
    },
    {
      id: "post-enrollment-followup",
      name: "Post Enrollment Follow-up Guide",
      description: "Important next steps and follow-up procedures after enrollment completion",
      filename: "post-enrollment-followup.pdf",
      category: "followup"
    }
  ]

  const handlePdfSelection = (pdfId: string) => {
    setSelectedPdfs(prev => {
      if (prev.includes(pdfId)) {
        return prev.filter(id => id !== pdfId)
      } else {
        return [...prev, pdfId]
      }
    })
  }

  const handleSendPdfs = async () => {
    if (!customerEmail || selectedPdfs.length === 0) {
      setErrorMessage("Please enter customer email and select at least one PDF")
      return
    }

    setIsLoading(true)
    setErrorMessage("")
    setSuccessMessage("")

    try {
      const selectedPdfResources = pdfResources.filter(pdf => selectedPdfs.includes(pdf.id))
      
      await AgentService.sharePdfResources({
        customerEmail,
        customerName: customerName || "Valued Customer",
        pdfResources: selectedPdfResources,
        emailSubject: emailSubject || "Important Resources for You"
      })

      setSuccessMessage(`Successfully sent ${selectedPdfs.length} PDF resource(s) to ${customerEmail}`)

      // Reset form after successful send
      setTimeout(() => {
        setCustomerEmail("")
        setCustomerName("")
        setSelectedPdfs([])
        setEmailSubject("")
        setSuccessMessage("")
      }, 3000)
    } catch (error) {
      setErrorMessage("Failed to send PDF resources. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const getPdfsByCategory = (category: string) => {
    return pdfResources.filter((pdf) => pdf.category === category)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-blue-100 p-2 rounded-lg">
            <Send className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Share Tools</h2>
            <p className="text-gray-600">Send personalized instructions, custom messages, and PDF resources to your customers</p>
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span className="text-green-800">{successMessage}</span>
          </div>
        )}

        {errorMessage && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-800">{errorMessage}</span>
          </div>
        )}

        {/* Customer Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="inline h-4 w-4 mr-1" />
              Customer Email *
            </label>
            <input
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="customer@example.com"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="inline h-4 w-4 mr-1" />
              Customer Name
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="John Doe"
            />
          </div>
        </div>

        {/* Email Subject */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <FileText className="inline h-4 w-4 mr-1" />
            Email Subject
          </label>
          <input
            type="text"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter email subject"
          />
        </div>
      </div>

            {/* PDF Resources Section */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="space-y-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Select PDF Resources to Send</h3>
            <p className="text-sm text-gray-600">Choose one or more PDF resources to send to your customer</p>
          </div>

          {/* PDF Categories */}
          {["gaming", "privacy", "followup"].map((category) => (
            <div key={category} className="space-y-3">
              <h4 className="text-md font-semibold text-gray-900 capitalize">
                {category === "gaming" ? "Gaming Guides" : 
                 category === "privacy" ? "Privacy Settings" : "Post Enrollment"}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {getPdfsByCategory(category).map((pdf) => (
                  <div
                    key={pdf.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedPdfs.includes(pdf.id)
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                    onClick={() => handlePdfSelection(pdf.id)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <FileText className="h-8 w-8 text-red-500" />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-900 mb-1">{pdf.name}</h5>
                        <p className="text-sm text-gray-600 mb-2">{pdf.description}</p>
                        <p className="text-xs text-gray-500 font-mono">{pdf.filename}</p>
                      </div>
                      <div className="flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={selectedPdfs.includes(pdf.id)}
                          onChange={() => handlePdfSelection(pdf.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {selectedPdfs.length > 0 && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Selected Resources ({selectedPdfs.length})</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                {selectedPdfs.map(pdfId => {
                  const pdf = pdfResources.find(p => p.id === pdfId)
                  return pdf ? (
                    <li key={pdfId} className="flex items-center space-x-2">
                      <FileText className="h-4 w-4" />
                      <span>{pdf.name}</span>
                    </li>
                  ) : null
                })}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Send Actions */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Send PDF Resources</h3>
            <p className="text-gray-600">
              Send {selectedPdfs.length} selected PDF resource(s) to customer
            </p>
          </div>
          <button
            onClick={handleSendPdfs}
            disabled={isLoading || !customerEmail || selectedPdfs.length === 0}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                <span>Send</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AgentShareInstructions
